import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { CheckIcon, PlusIcon } from '@/components/icons';
import type { Message, TaskListSummary } from '@/types/api';

/**
 * In-bubble render of a Telegram-style task list (checklist). Frameless to
 * match the poll bubble: icon + title + progress, then check-off rows. Toggling
 * an item PUTs to the server, which broadcasts a message:update so every viewer
 * updates live; we also splice the response into the store for an instant local
 * update. `allowComplete` (or being the author) gates checking; `allowAdd` (or
 * author) shows an append-item input.
 */
export function TaskListBubble(props: {
  taskList: TaskListSummary;
  messageId: string;
  channelId: string;
  messageAuthorId: string;
}) {
  const { t } = useTranslation();
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const upsertMessage = useDataStore((s) => s.upsertMessage);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const list = props.taskList;

  const isMine = meId === props.messageAuthorId;
  const canComplete = list.allowComplete || isMine;
  const canAdd = list.allowAdd || isMine;

  /** Replace the parent message in the store with the server's updated copy
   *  (keeps author/reactions/etc; just swaps the fresh taskList + meta). */
  function applyMessage(updated: Message) {
    upsertMessage(updated);
  }

  async function toggle(itemId: string) {
    if (!canComplete || busy) return;
    setBusy(true);
    try {
      applyMessage(await api.toggleTaskItem(list.id, itemId));
    } catch (err) {
      toast.error(...errToToast(err, t('cmod.tasklist.toggleError')));
    } finally {
      setBusy(false);
    }
  }

  async function addItem() {
    const label = newLabel.trim();
    if (!label || busy) return;
    setBusy(true);
    try {
      applyMessage(await api.addTaskItem(list.id, label));
      setNewLabel('');
      setAdding(false);
    } catch (err) {
      toast.error(...errToToast(err, t('cmod.tasklist.addError')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="studio-task-list mt-1.5 flex min-w-0 flex-col gap-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <CheckIcon className="h-3.5 w-3.5" />
        <span>
          {t('cmod.tasklist.header', { done: list.doneCount, total: list.total })}
        </span>
      </div>
      <div className="text-sm font-medium tracking-[-0.01em] text-foreground">{list.title}</div>

      <div className="flex flex-col gap-0.5">
        {list.items.map((it) => {
          const done = it.completedAt !== null;
          return (
            <button
              key={it.id}
              type="button"
              disabled={!canComplete || busy}
              aria-pressed={done}
              onClick={() => void toggle(it.id)}
              className={
                'flex items-start gap-2.5 rounded-lg px-1.5 py-1.5 text-left transition-colors ' +
                (canComplete ? 'hover:bg-default/40' : 'cursor-default')
              }
            >
              <span
                className={
                  'mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border transition-colors ' +
                  (done ? 'border-accent bg-accent text-white' : 'border-ink-muted/50 text-transparent')
                }
              >
                <CheckIcon className="h-3 w-3" />
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={
                    'block text-sm ' +
                    (done ? 'text-ink-muted line-through' : 'text-foreground/90')
                  }
                >
                  {it.label}
                </span>
                {done && it.completedBy && (
                  <span className="block text-xs text-muted">
                    {it.completedBy.displayName || it.completedBy.username}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {canAdd &&
        (adding ? (
          <input
            autoFocus
            value={newLabel}
            maxLength={120}
            disabled={busy}
            aria-label={t('cmod.tasklist.newItemAria')}
            onChange={(e) => setNewLabel(e.target.value)}
            onBlur={() => void addItem()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void addItem();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setAdding(false);
                setNewLabel('');
              }
            }}
            placeholder={t('cmod.tasklist.newItemPlaceholder')}
            className="rounded-lg border border-separator bg-default/40 px-2.5 py-1.5 text-sm text-ink outline-none placeholder:text-ink-muted focus:border-accent"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 self-start rounded-lg px-1.5 py-1 text-xs text-accent transition-colors hover:bg-accent/10"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {t('cmod.tasklist.addItem')}
          </button>
        ))}
    </div>
  );
}
